/**
 * UI 管理器
 * 统一管理游戏界面元素
 */
import { GameConfig } from '../types';
import { SaveManager, SaveSlot } from '../storage/SaveManager';
import { i18n } from '../core/I18nManager';

export interface UIElements {
    // 控制面板
    seedInput?: HTMLInputElement;
    seedSlider?: HTMLInputElement;
    tabSizeInput?: HTMLInputElement;
    tabSizeSlider?: HTMLInputElement;
    jitterInput?: HTMLInputElement;
    jitterSlider?: HTMLInputElement;
    ringsInput?: HTMLInputElement;
    warpCheckbox?: HTMLInputElement;

    // 主题
    themeSelect?: HTMLSelectElement;

    // 按钮
    shuffleBtn?: HTMLButtonElement;
    organizeBtn?: HTMLButtonElement;
    saveBtn?: HTMLButtonElement;
    loadBtn?: HTMLButtonElement;
    newGameBtn?: HTMLButtonElement;

    // 信息显示
    timerDisplay?: HTMLElement;
    progressDisplay?: HTMLElement;
    moveCountDisplay?: HTMLElement;
}

export interface UIEvents {
    onConfigChange?: (config: Partial<GameConfig>) => void;
    onThemeChange?: (themeName: string) => void;
    onShuffle?: () => void;
    onOrganize?: () => void;
    onSave?: () => void;
    onLoad?: (slotId: string) => void;
    onNewGame?: () => void;
    onUndo?: () => void;
}

export class UIManager {
    private elements: UIElements = {};
    private events: UIEvents = {};
    private timerInterval: number | null = null;
    private elapsedTime: number = 0;
    private isTimerRunning: boolean = false;

    constructor() {}

    /**
     * 初始化 UI 元素
     */
    initialize(elements: UIElements): void {
        this.elements = elements;
        this.bindEvents();
    }

    /**
     * 设置事件监听
     */
    setEvents(events: UIEvents): void {
        this.events = { ...this.events, ...events };
    }

    /**
     * 绑定事件
     */
    private bindEvents(): void {
        // 主题切换
        this.elements.themeSelect?.addEventListener('change', () => {
            const themeName = this.elements.themeSelect!.value;
            this.events.onThemeChange?.(themeName);
        });

        // Seed 控制
        this.elements.seedSlider?.addEventListener('input', () => {
            const value = this.elements.seedSlider!.value;
            if (this.elements.seedInput) {
                this.elements.seedInput.value = value;
            }
            this.events.onConfigChange?.({ seed: parseInt(value) });
        });

        this.elements.seedInput?.addEventListener('change', () => {
            const value = this.elements.seedInput!.value;
            if (this.elements.seedSlider) {
                this.elements.seedSlider.value = value;
            }
            this.events.onConfigChange?.({ seed: parseInt(value) });
        });

        // Tab Size 控制
        this.elements.tabSizeSlider?.addEventListener('input', () => {
            const value = parseFloat(this.elements.tabSizeSlider!.value);
            if (this.elements.tabSizeInput) {
                this.elements.tabSizeInput.value = value + '%';
            }
            this.events.onConfigChange?.({ tabSize: value / 200 });
        });

        // Jitter 控制
        this.elements.jitterSlider?.addEventListener('input', () => {
            const value = parseFloat(this.elements.jitterSlider!.value);
            if (this.elements.jitterInput) {
                this.elements.jitterInput.value = value + '%';
            }
            this.events.onConfigChange?.({ jitter: value / 100 });
        });

        // Rings 控制
        this.elements.ringsInput?.addEventListener('change', () => {
            const value = parseInt(this.elements.ringsInput!.value);
            this.events.onConfigChange?.({ rings: value });
        });

        // Warp 控制
        this.elements.warpCheckbox?.addEventListener('change', () => {
            const checked = this.elements.warpCheckbox!.checked;
            this.events.onConfigChange?.({ enableWarp: checked });
        });

        // 按钮
        this.elements.shuffleBtn?.addEventListener('click', () => {
            this.events.onShuffle?.();
        });

        this.elements.organizeBtn?.addEventListener('click', () => {
            this.events.onOrganize?.();
        });

        this.elements.saveBtn?.addEventListener('click', () => {
            this.events.onSave?.();
        });

        this.elements.loadBtn?.addEventListener('click', () => {
            this.showLoadDialog();
        });

        this.elements.newGameBtn?.addEventListener('click', () => {
            this.events.onNewGame?.();
        });

        // 键盘快捷键
        document.addEventListener('keydown', (e) => {
            if (e.ctrlKey || e.metaKey) {
                switch (e.key.toLowerCase()) {
                    case 'z':
                        e.preventDefault();
                        this.events.onUndo?.();
                        break;
                    case 's':
                        e.preventDefault();
                        this.events.onSave?.();
                        break;
                }
            }
        });
    }

    /**
     * 更新配置显示
     */
    updateConfigDisplay(config: GameConfig): void {
        if (this.elements.seedInput) {
            this.elements.seedInput.value = String(config.seed);
        }
        if (this.elements.seedSlider) {
            this.elements.seedSlider.value = String(config.seed);
        }
        if (this.elements.tabSizeInput) {
            this.elements.tabSizeInput.value = (config.tabSize * 200) + '%';
        }
        if (this.elements.tabSizeSlider) {
            this.elements.tabSizeSlider.value = String(config.tabSize * 200);
        }
        if (this.elements.jitterInput) {
            this.elements.jitterInput.value = (config.jitter * 100) + '%';
        }
        if (this.elements.jitterSlider) {
            this.elements.jitterSlider.value = String(config.jitter * 100);
        }
        if (this.elements.ringsInput) {
            this.elements.ringsInput.value = String(config.rings);
        }
        if (this.elements.warpCheckbox) {
            this.elements.warpCheckbox.checked = config.enableWarp;
        }
    }

    /**
     * 更新进度显示
     */
    updateProgress(placed: number, total: number): void {
        if (this.elements.progressDisplay) {
            const percent = Math.round((placed / total) * 100);
            this.elements.progressDisplay.textContent = `${placed}/${total} (${percent}%)`;
        }
    }

    /**
     * 更新移动次数
     */
    updateMoveCount(count: number): void {
        if (this.elements.moveCountDisplay) {
            this.elements.moveCountDisplay.textContent = `${i18n.t('moves_label')}: ${count}`;
        }
    }

    /**
     * 开始计时器
     */
    startTimer(initialTime: number = 0): void {
        this.elapsedTime = initialTime;
        this.isTimerRunning = true;

        this.updateTimerDisplay();

        if (this.timerInterval !== null) {
            clearInterval(this.timerInterval);
        }

        this.timerInterval = window.setInterval(() => {
            if (this.isTimerRunning) {
                this.elapsedTime += 1000;
                this.updateTimerDisplay();
            }
        }, 1000);
    }

    /**
     * 暂停计时器
     */
    pauseTimer(): void {
        this.isTimerRunning = false;
    }

    /**
     * 继续计时器
     */
    resumeTimer(): void {
        this.isTimerRunning = true;
    }

    /**
     * 停止计时器
     */
    stopTimer(): void {
        this.isTimerRunning = false;
        if (this.timerInterval !== null) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
    }

    /**
     * 获取已过时间
     */
    getElapsedTime(): number {
        return this.elapsedTime;
    }

    /**
     * 更新计时器显示
     */
    private updateTimerDisplay(): void {
        if (this.elements.timerDisplay) {
            this.elements.timerDisplay.textContent = SaveManager.formatTime(this.elapsedTime);
        }
    }

    /**
     * 显示加载对话框
     */
    showLoadDialog(): void {
        const saveManager = SaveManager.getInstance();
        const slots = saveManager.getSaveSlots();

        if (slots.length === 0) {
            this.showMessage(i18n.t('msg_no_save_found'), 'info');
            return;
        }

        const dialog = document.createElement('div');
        dialog.className = 'modal-overlay';
        dialog.innerHTML = `
            <div class="modal-dialog">
                <div class="modal-header">
                    <h3>${i18n.t('load_save')}</h3>
                    <button class="modal-close">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="save-slots">
                        ${slots.map(slot => `
                            <div class="save-slot" data-id="${slot.id}">
                                <div class="slot-info">
                                    <strong>${slot.name}</strong>
                                    <span>${SaveManager.formatDate(slot.timestamp)}</span>
                                </div>
                                <div class="slot-stats">
                                    <span>${i18n.t('progress_label')}: ${slot.placedCount}/${slot.pieceCount}</span>
                                    <span>${i18n.t('time_label')}: ${SaveManager.formatTime(slot.elapsedTime)}</span>
                                </div>
                                <button class="slot-delete" data-id="${slot.id}">${i18n.t('delete')}</button>
                            </div>
                        `).join('')}
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(dialog);

        // 关闭按钮
        dialog.querySelector('.modal-close')?.addEventListener('click', () => {
            document.body.removeChild(dialog);
        });

        // 点击背景关闭
        dialog.addEventListener('click', (e) => {
            if (e.target === dialog) {
                document.body.removeChild(dialog);
            }
        });

        // 加载存档
        dialog.querySelectorAll('.save-slot').forEach(slot => {
            slot.addEventListener('click', (e) => {
                if ((e.target as HTMLElement).classList.contains('slot-delete')) return;

                const slotId = slot.getAttribute('data-id');
                if (slotId) {
                    this.events.onLoad?.(slotId);
                    document.body.removeChild(dialog);
                }
            });
        });

        // 删除存档
        dialog.querySelectorAll('.slot-delete').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const slotId = (e.target as HTMLElement).getAttribute('data-id');
                if (slotId && confirm(i18n.t('confirm_delete_save'))) {
                    saveManager.delete(slotId);
                    const slotElement = dialog.querySelector(`[data-id="${slotId}"]`);
                    slotElement?.remove();

                    if (dialog.querySelectorAll('.save-slot').length === 0) {
                        document.body.removeChild(dialog);
                        this.showMessage(i18n.t('msg_all_saves_deleted'), 'info');
                    }
                }
            });
        });
    }

    /**
     * 显示保存对话框
     */
    showSaveDialog(onSave: (slotId: string) => void): void {
        const slotId = `save_${Date.now()}`;

        // 简化：直接保存到新槽位
        // 可以扩展为让用户选择槽位或输入名称
        onSave(slotId);
        this.showMessage(i18n.t('msg_game_saved'), 'success');
    }

    /**
     * 显示消息提示
     */
    showMessage(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        toast.textContent = message;

        document.body.appendChild(toast);

        // 动画显示
        requestAnimationFrame(() => {
            toast.classList.add('show');
        });

        // 自动隐藏
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => {
                document.body.removeChild(toast);
            }, 300);
        }, 3000);
    }

    /**
     * 显示游戏完成对话框
     * 返回 updateScore 回调，供 API 返回后异步填入得分和排名
     */
    showCompleteDialog(data: {
        time: number;
        difficulty: string;
        pieceCount: number;
        puzzleId?: string;
        puzzleName?: string;
    }): { updateScore: (score: number, rank: number, isNewBest: boolean) => void; showLoginHint: () => void; hideLoginHint: () => void } {
        const diffKey = `diff_${data.difficulty}`;
        const dialog = document.createElement('div');
        dialog.className = 'modal-overlay';
        dialog.innerHTML = `
            <div class="modal-dialog complete-dialog">
                <div class="complete-icon">🎉</div>
                <h2>${i18n.t('congrats')}</h2>
                <div class="complete-stats">
                    <p class="complete-stat-sub">${i18n.t(diffKey)} · ${data.pieceCount} ${i18n.t('pieces_label')}</p>
                    <div class="complete-stat-row">
                        <span class="complete-stat-label">${i18n.t('timer_label')}</span>
                        <span class="complete-stat-value">${SaveManager.formatTime(data.time)}</span>
                    </div>
                    <div class="complete-stat-row" id="complete-score-row">
                        <span class="complete-stat-label">${i18n.t('score_label')}</span>
                        <span class="complete-stat-value" id="complete-score-value">...</span>
                    </div>
                    <div class="complete-stat-row" id="complete-rank-row">
                        <span class="complete-stat-label">${i18n.t('rank_label')}</span>
                        <span class="complete-stat-value" id="complete-rank-value">...</span>
                    </div>
                </div>
                <div id="complete-new-record" class="complete-new-record">
                    ${i18n.t('new_record')}
                </div>
                <div id="complete-login-hint" class="complete-login-hint" style="display:none;">
                    <span>${i18n.t('login_to_save_score')}</span>
                    <button class="btn-login-small" id="complete-login-btn">${i18n.t('login')}</button>
                </div>
                <div class="complete-share">
                    <span class="complete-share-label">${i18n.t('share_result') || 'Share'}</span>
                    <div class="complete-share-buttons">
                        <button id="share-facebook" class="share-btn share-btn-facebook" title="Facebook">
                            <iconify-icon icon="mdi:facebook" width="20"></iconify-icon>
                        </button>
                        <button id="share-x" class="share-btn share-btn-x" title="X">
                            <iconify-icon icon="ri:twitter-x-fill" width="18"></iconify-icon>
                        </button>
                    </div>
                </div>
                <div class="complete-actions">
                    <button class="btn btn-primary" id="complete-new-game">${i18n.t('start_new_game')}</button>
                    <button class="btn btn-secondary" id="complete-close">${i18n.t('close')}</button>
                </div>
            </div>
        `;

        document.body.appendChild(dialog);

        dialog.querySelector('#complete-new-game')?.addEventListener('click', () => {
            document.body.removeChild(dialog);
            this.events.onNewGame?.();
        });

        dialog.querySelector('#complete-close')?.addEventListener('click', () => {
            document.body.removeChild(dialog);
        });

        // 登录按钮点击 → 弹出登录框
        dialog.querySelector('#complete-login-btn')?.addEventListener('click', () => {
            (window as any).authModal?.show('login');
        });

        // 分享功能
        const buildShareData = () => {
            const timeText = SaveManager.formatTime(data.time);
            const scoreEl = dialog.querySelector('#complete-score-value');
            const score = scoreEl?.textContent || '';
            const name = data.puzzleName || 'Jigsaw Hex';
            const pageUrl = data.puzzleId
                ? `${window.location.origin}/game.html?id=${data.puzzleId}`
                : window.location.origin;
            const text = i18n.t('share_text')
                ?.replace('{name}', name)
                ?.replace('{time}', timeText)
                ?.replace('{pieces}', String(data.pieceCount))
                ?.replace('{score}', score)
                || `I completed "${name}" (${data.pieceCount} pieces) in ${timeText}! Score: ${score} #JigsawHex`;
            return { text, url: pageUrl };
        };

        dialog.querySelector('#share-facebook')?.addEventListener('click', () => {
            const { url } = buildShareData();
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
        });

        dialog.querySelector('#share-x')?.addEventListener('click', () => {
            const { text, url } = buildShareData();
            window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
        });

        return {
            showLoginHint: () => {
                const hintEl = dialog.querySelector('#complete-login-hint') as HTMLElement | null;
                const scoreRow = dialog.querySelector('#complete-score-row') as HTMLElement | null;
                const rankRow = dialog.querySelector('#complete-rank-row') as HTMLElement | null;
                if (hintEl) hintEl.style.display = '';
                if (scoreRow) scoreRow.style.display = 'none';
                if (rankRow) rankRow.style.display = 'none';
            },
            hideLoginHint: () => {
                const hintEl = dialog.querySelector('#complete-login-hint') as HTMLElement | null;
                const scoreRow = dialog.querySelector('#complete-score-row') as HTMLElement | null;
                const rankRow = dialog.querySelector('#complete-rank-row') as HTMLElement | null;
                if (hintEl) hintEl.style.display = 'none';
                if (scoreRow) scoreRow.style.display = '';
                if (rankRow) rankRow.style.display = '';
            },
            updateScore: (score: number, rank: number, isNewBest: boolean) => {
                const scoreEl = dialog.querySelector('#complete-score-value');
                const rankEl = dialog.querySelector('#complete-rank-value');
                const recordEl = dialog.querySelector('#complete-new-record') as HTMLElement | null;

                // 得分 countUp 动画
                if (scoreEl) {
                    const duration = 800;
                    const start = performance.now();
                    const step = (now: number) => {
                        const progress = Math.min((now - start) / duration, 1);
                        const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
                        scoreEl.textContent = Math.floor(score * eased).toLocaleString();
                        if (progress < 1) requestAnimationFrame(step);
                    };
                    requestAnimationFrame(step);
                }

                // 排名淡入
                if (rankEl) {
                    rankEl.textContent = `#${rank}`;
                    (rankEl as HTMLElement).style.transition = 'opacity 0.3s ease';
                    (rankEl as HTMLElement).style.opacity = '0';
                    requestAnimationFrame(() => { (rankEl as HTMLElement).style.opacity = '1'; });
                }

                // 新纪录标记弹入
                if (recordEl && isNewBest) {
                    recordEl.classList.add('show');
                }
            }
        };
    }

    /**
     * 销毁
     */
    destroy(): void {
        this.stopTimer();
    }
}
